import {
  Button,
  makeStyles,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  tokens,
} from '@fluentui/react-components';
import { LightbulbRegular, QuestionCircleRegular } from '@fluentui/react-icons';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';

import suggestedQuestions from '../../../../../src/framework-v2/csharp/Core/Stages/Report/Renderers/Html/Assets/agency-questions.json';

import { buildHostedPrompt } from './hostedPrompt.js';
import type { HostedReportSource } from './hostedPrompt.js';

export interface AskAgencyProps {
  source: HostedReportSource;
}

const useStyles = makeStyles({
  panel: {
    margin: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    flexShrink: 0,
  },
  heading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalXS,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  title: {
    margin: 0,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  composer: {
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusXLarge,
    padding: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground1,
    ':focus-within': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '2px',
    },
  },
  field: {
    display: 'block',
    boxSizing: 'border-box',
    width: '100%',
    minHeight: '40px',
    height: '40px',
    maxHeight: '100px',
    lineHeight: '20px',
    border: 'none',
    outline: '0',
    padding: 0,
    fontFamily: 'inherit',
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    backgroundColor: 'transparent',
    resize: 'none',
    overflowY: 'auto',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalXS,
  },
  status: {
    flex: 1,
    minWidth: 0,
    margin: 0,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
  help: {
    boxSizing: 'border-box',
    width: 'min(440px, calc(100vw - 32px))',
    maxHeight: 'min(600px, 80vh)',
    overflowY: 'auto',
  },
  fallback: {
    boxSizing: 'border-box',
    width: '100%',
    marginTop: tokens.spacingVerticalS,
    fontFamily: 'inherit',
  },
});

function resizeQuestion(input: HTMLTextAreaElement) {
  input.style.height = 'auto';
  input.style.height = `${Math.min(100, Math.max(40, input.scrollHeight))}px`;
}

export function AskAgency({ source }: AskAgencyProps) {
  const styles = useStyles();
  const id = useId();
  const [question, setQuestion] = useState('');
  const [prompt, setPrompt] = useState('');
  const [status, setStatus] = useState('');
  const [helpOpen, setHelpOpen] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const questionInput = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    if (questionInput.current) resizeQuestion(questionInput.current);
  }, [question]);
  useEffect(() => {
    const resize = () => {
      if (questionInput.current) resizeQuestion(questionInput.current);
    };
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);
  let unavailable = '';
  try {
    buildHostedPrompt(source, '');
  } catch (error) {
    unavailable =
      error instanceof Error
        ? error.message
        : 'Invalid AI Studio API configuration.';
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPrompt('');
    if (unavailable) return;
    if (!question.trim()) {
      setStatus('Enter a question before opening Agency.');
      return;
    }
    let link: HTMLAnchorElement | undefined;
    try {
      const nextPrompt = buildHostedPrompt(source, question);
      setPrompt(nextPrompt);
      const href =
        'agency://copilot?interactive=' + encodeURIComponent(nextPrompt);
      if (href.length > 8000) {
        setStatus(
          'The link exceeds 8,000 characters. Shorten the question or open help to copy the prompt.',
        );
        return;
      }
      link = document.createElement('a');
      link.href = href;
      link.hidden = true;
      document.body.append(link);
      setStatus('');
      link.click();
    } catch (error) {
      setStatus(
        `Unable to request Agency: ${error instanceof Error ? error.message : String(error)}. Open help to copy the prompt.`,
      );
    } finally {
      link?.remove();
    }
  }

  return (
    <section className={styles.panel} aria-labelledby={`${id}-title`}>
      <div className={styles.heading}>
        <h3 className={styles.title} id={`${id}-title`}>
          Ask Agency (preview)
        </h3>
        <div className={styles.actions}>
          <Menu
            open={suggestionsOpen}
            onOpenChange={(_, data) => {
              setSuggestionsOpen(data.open);
              if (data.open) setHelpOpen(false);
            }}
          >
            <MenuTrigger disableButtonEnhancement>
              <Button
                type="button"
                appearance="subtle"
                size="small"
                icon={<LightbulbRegular />}
                aria-label="Suggested questions"
                title="Suggested questions"
                disabled={!!unavailable}
              />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                {suggestedQuestions.map(({ label, question: text }) => (
                  <MenuItem
                    key={label}
                    onClick={() => {
                      setQuestion(text);
                      setStatus('');
                      setPrompt('');
                      setSuggestionsOpen(false);
                      requestAnimationFrame(() =>
                        questionInput.current?.focus(),
                      );
                    }}
                  >
                    {label}
                  </MenuItem>
                ))}
              </MenuList>
            </MenuPopover>
          </Menu>
          <Popover
            open={helpOpen}
            onOpenChange={(_, data) => {
              setHelpOpen(data.open);
              if (data.open) setSuggestionsOpen(false);
            }}
            positioning="below-end"
            trapFocus
          >
            <PopoverTrigger disableButtonEnhancement>
              <Button
                type="button"
                appearance="subtle"
                size="small"
                icon={<QuestionCircleRegular />}
                aria-label="Ask Agency help"
                title="Ask Agency help"
              />
            </PopoverTrigger>
            <PopoverSurface
              className={styles.help}
              role="dialog"
              aria-label="Ask Agency help"
            >
              <div className={styles.heading}>
                <strong>Ask Agency help</strong>
                <Button
                  appearance="subtle"
                  size="small"
                  onClick={() => setHelpOpen(false)}
                >
                  Close help
                </Button>
              </div>
              <p>
                Ask about this report in the installed Agency app. Only your
                question and retrieval instructions are passed to Agency. Agency
                uses your Azure CLI sign-in to download the result JSON locally
                and analyze it in the same session.
              </p>
              <p>
                Browser, authentication and tool approvals may be required.
                Analysis may use an online AI service. Answers appear in Agency,
                not here. This report cannot confirm that Agency opened. If
                nothing happens, copy the prompt into Agency manually.
              </p>
              {prompt && (
                <>
                  <label htmlFor={`${id}-prompt`}>
                    Select and copy this prompt into Agency manually
                  </label>
                  <textarea
                    id={`${id}-prompt`}
                    className={styles.fallback}
                    rows={6}
                    readOnly
                    value={prompt}
                  />
                </>
              )}
            </PopoverSurface>
          </Popover>
        </div>
      </div>
      <form onSubmit={submit} className={styles.composer}>
        <textarea
          ref={questionInput}
          id={`${id}-question`}
          className={styles.field}
          rows={2}
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          disabled={!!unavailable}
          aria-label="Question about this report"
          aria-describedby={`${id}-status`}
          placeholder="What should be my main takeaways from this evaluation run?"
        />
        <div className={styles.footer}>
          <p
            id={`${id}-status`}
            className={styles.status}
            role="status"
            aria-live="polite"
          >
            {unavailable || status}
          </p>
          <Button
            type="submit"
            size="small"
            appearance="primary"
            disabled={!!unavailable}
          >
            Ask Agency
          </Button>
        </div>
      </form>
    </section>
  );
}
